import React, { useState, useEffect } from 'react';
import { Users, Crown, TrendingUp, DollarSign, Settings, FileText } from 'lucide-react';
import { statsApi } from '../services/api';
import { Stats } from '../types';
import PointManagementModal from './PointManagementModal';
import PointLogs from './PointLogs';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPointModalOpen, setIsPointModalOpen] = useState(false);
  const [isPointLogsOpen, setIsPointLogsOpen] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await statsApi.getStats();
        if (response.success) {
          setStats(response.data);
        } else {
          setError('통계를 불러오는데 실패했습니다.');
        }
      } catch (err) {
        setError('서버 연결에 실패했습니다.');
        console.error('Stats fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <div className="text-gray-500">데이터가 없습니다.</div>
      </div>
    );
  }

  const statCards = [
    {
      title: '전체 사용자',
      value: stats.users.total.toLocaleString(),
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: '활성 사용자',
      value: stats.users.active.toLocaleString(),
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: '프리미엄 사용자',
      value: stats.users.premium.toLocaleString(),
      icon: Crown,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: '최근 가입자 (7일)',
      value: stats.users.recent.toLocaleString(),
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">대시보드</h1>
        <p className="mt-2 text-gray-600">시스템 현황을 한눈에 확인하세요</p>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-full ${card.bgColor}`}>
                  <Icon className={`h-6 w-6 ${card.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 포인트 통계 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <DollarSign className="h-6 w-6 text-green-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">포인트 현황</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsPointModalOpen(true)}
                className="flex items-center px-3 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
              >
                <Settings className="h-4 w-4 mr-1" />
                포인트 관리
              </button>
              <button
                onClick={() => setIsPointLogsOpen(true)}
                className="flex items-center px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              >
                <FileText className="h-4 w-4 mr-1" />
                포인트 로그
              </button>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">총 보유 포인트</span>
              <span className="font-semibold">{stats.points.totalPoints.toLocaleString()}P</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">총 적립 포인트</span>
              <span className="font-semibold text-green-600">{stats.points.totalEarned.toLocaleString()}P</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">총 사용 포인트</span>
              <span className="font-semibold text-red-600">{stats.points.totalSpent.toLocaleString()}P</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">평균 보유 포인트</span>
              <span className="font-semibold">{Math.round(stats.points.avgPoints).toLocaleString()}P</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">사용량 현황</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">오늘 총 사용량</span>
              <span className="font-semibold text-blue-600">
                {stats.usage?.totalDailyUsed?.toLocaleString() || '0'}회
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">이번 달 총 사용량</span>
              <span className="font-semibold text-green-600">
                {stats.usage?.totalMonthlyUsed?.toLocaleString() || '0'}회
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">사용자당 평균 일일 사용량</span>
              <span className="font-semibold">{stats.usage?.avgDailyUsed?.toFixed(1) || '0.0'}회</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">사용자당 평균 월간 사용량</span>
              <span className="font-semibold">{stats.usage?.avgMonthlyUsed?.toFixed(1) || '0.0'}회</span>
            </div>
          </div>
        </div>
      </div>

      {/* 포인트 관리 모달 */}
      <PointManagementModal isOpen={isPointModalOpen} onClose={() => setIsPointModalOpen(false)} />

      {/* 포인트 로그 모달 */}
      {isPointLogsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-7xl w-full max-h-[90vh] overflow-hidden">
            <PointLogs onClose={() => setIsPointLogsOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
